import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Calendar, Award, ChevronRight, Loader2 } from "lucide-react";

export default function AlumnoTareas() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/alumno/assignments")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const pending = items.filter(a => !a.my_delivery || a.my_delivery.status === "returned_for_resubmission");
  const submitted = items.filter(a => a.my_delivery && a.my_delivery.status !== "returned_for_resubmission");

  return (
    <AlumnoLayout user={user}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Alumno</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Mis tareas</h1>
        <p className="mt-1 text-sm text-gray-400">Entrega asignaciones, revisa retroalimentación y descarga material.</p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="space-y-8">
          <TareaGroup title="Pendientes" empty="Sin tareas pendientes." items={pending} />
          <TareaGroup title="Entregadas" empty="Sin entregas registradas." items={submitted} />
        </div>
      )}
    </AlumnoLayout>
  );
}

function TareaGroup({ title, items, empty }) {
  return (
    <div>
      <h2 className="text-sm uppercase tracking-widest text-gray-400 font-bold mb-2">{title} ({items.length})</h2>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-gray-400">{empty}</div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <Link key={a.id} to={`/app/alumno/tareas/${a.id}`}
                  data-testid={`alumno-tarea-row-${a.id}`}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08]">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">{a.title}</p>
                {a.due_at && (
                  <p className={`mt-1 text-[11px] inline-flex items-center gap-1 ${a.overdue ? "text-red-300" : "text-gray-400"}`}>
                    <Calendar className="w-3 h-3" /> Entrega: {new Date(a.due_at).toLocaleString("es-MX")}
                    {a.overdue && <span className="ml-1 uppercase tracking-widest font-bold">Atrasada</span>}
                  </p>
                )}
                {a.my_delivery?.status === "graded" && a.my_delivery.score !== null && (
                  <p className="mt-1 text-xs text-emerald-300 font-semibold inline-flex items-center gap-1">
                    <Award className="w-3 h-3" /> {a.my_delivery.score}/{a.max_score}
                  </p>
                )}
                {a.my_delivery?.status === "returned_for_resubmission" && (
                  <p className="mt-1 text-xs text-amber-300 font-semibold">Devuelta para reenvío</p>
                )}
                <p className="mt-1 text-xs text-gray-400 line-clamp-2">{a.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0 mt-2" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
